// ─── Context-band derivation (#770, story-first IA) ─────────────────────
// Pure derivations from the queue read model the workspace already holds —
// no extra fetch. Three answers the band gives at every station:
//   THE STORY   — what happened and why each dollar amount exists
//   NEEDED TO FINISH — the job's remaining musts, in station order
//   MONEY       — invoice · estimate · still-to-collect, co-visible
// Money strings arrive pre-redacted (null) for employee-tier viewers, so
// everything here degrades to word-only labels without a dollar in sight.
// Corbin's steer (threads/story-first-ia/FEEDBACK.md): "the story needs to
// be the high points of what happened so just glancing at it we understand
// what is going on without having to dig."

import { composeStoryLine } from "@/lib/job-story";
import type { QueueJob } from "./queue-list";

export interface BandNeed {
    station: 1 | 3 | 4 | 5;
    label: string;
    state: "done" | "current" | "todo";
}

export interface BandModel {
    /** High-point beats, pre-joined: "2 tested · 1 failed → …" */
    story: string;
    needs: BandNeed[];
    money: {
        invoice: string | null;
        estimate: string | null;
        due: string | null;
    };
}

function fmtMoney(raw: string | null): string | null {
    if (raw == null) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function deriveBandModel(j: QueueJob | null): BandModel | null {
    if (!j) return null;

    const invoice = fmtMoney(j.open_invoice_total) ?? fmtMoney(j.amount_due);
    const estimate = fmtMoney(j.draft_estimate_total);
    const due = j.unpaid ? fmtMoney(j.amount_due) : null;

    // ── The story: who/when → what happened → why the dollar amounts ──
    const parts: string[] = [];
    const when = fmtDate(j.completed_at);
    const head = [j.job_type_name ?? "Job", when, j.tech_name].filter(Boolean).join(" · ");
    if (head) parts.push(head);
    if (j.tested_devices > 0) {
        parts.push(
            j.failed_devices > 0
                ? `${j.tested_devices} tested, ${j.failed_devices} failed`
                : `${j.tested_devices} tested, all passed`,
        );
    }
    const moneyBits: string[] = [];
    if (invoice) moneyBits.push(`${invoice} invoice`);
    if (j.draft_estimate_count > 0) moneyBits.push(estimate ? `${estimate} repair quote` : "a repair quote");
    // Composition (the " — " beats + "→ money" causality arrow) is the shared
    // story service — the job page's NOW panel speaks the same line (#767).
    const story = composeStoryLine(parts, moneyBits);

    // ── Needed to finish: fixed station order, server's blocking = truth ──
    const hasEstimateStep = j.failed_devices > 0 || j.draft_estimate_count > 0;
    const needs: BandNeed[] = [];
    const stateFor = (station: number): BandNeed["state"] =>
        j.blocking.station > station ? "done" : j.blocking.station === station ? "current" : "todo";

    needs.push({
        station: 1,
        label:
            stateFor(1) === "current"
                ? j.blocking.label || "Review the documents"
                : stateFor(1) === "done"
                  ? "Documents reviewed"
                  : "Review the documents",
        state: stateFor(1),
    });
    if (hasEstimateStep) {
        needs.push({
            station: 3,
            label: stateFor(3) === "done" ? "Estimate approved" : "Get the estimate approved",
            state: stateFor(3),
        });
    }
    needs.push({
        station: 4,
        label: !j.unpaid ? "Paid" : due ? `Collect ${due}` : "Collect payment",
        state: !j.unpaid && stateFor(4) !== "current" ? "done" : stateFor(4),
    });
    needs.push({
        station: 5,
        label: `Send packet${j.site_city ? ` + file with ${j.site_city}` : " + file with the city"}`,
        state: stateFor(5),
    });

    return { story, needs, money: { invoice, estimate, due } };
}
